import { useState, useEffect } from 'react';
import { X, CreditCard, Calendar, Users, Bed, CheckCircle, AlertCircle } from 'lucide-react';
import {
  loadRazorpayScript,
  initializeRazorpay,
  calculateBookingAmount,
  sendBookingConfirmation,
  ROOM_PRICES,
  type BookingDetails,
} from '../services/razorpayService';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  preselectedRoomType?: 'Single' | 'Royal' | 'Suite';
}

export function BookingModal({ isOpen, onClose, preselectedRoomType }: BookingModalProps) {
  const [step, setStep] = useState<'details' | 'summary' | 'success' | 'error'>('details');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentId, setPaymentId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [formData, setFormData] = useState<BookingDetails>({
    name: '',
    email: '',
    phone: '',
    checkIn: '',
    checkOut: '',
    guests: 1,
    roomType: preselectedRoomType || 'Royal',
    specialRequests: '',
    idProofType: '',
    idProofNumber: '',
  });

  const [calculatedAmount, setCalculatedAmount] = useState(0);

  // Calculate amount when relevant fields change
  useEffect(() => {
    if (formData.checkIn && formData.checkOut && formData.roomType) {
      const amount = calculateBookingAmount(
        formData.roomType,
        formData.checkIn,
        formData.checkOut,
        formData.guests
      );
      setCalculatedAmount(amount);
    }
  }, [formData.checkIn, formData.checkOut, formData.roomType, formData.guests]);

  // Load Razorpay script on mount
  useEffect(() => {
    if (isOpen) {
      loadRazorpayScript();
    }
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'guests' ? parseInt(value) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate dates
    const checkIn = new Date(formData.checkIn);
    const checkOut = new Date(formData.checkOut);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (checkIn < today) {
      setErrorMessage('Check-in date cannot be in the past');
      setStep('error');
      return;
    }

    if (checkOut <= checkIn) {
      setErrorMessage('Check-out date must be after check-in date');
      setStep('error');
      return;
    }

    setStep('summary');
  };

  const handlePayment = async () => {
    setIsProcessing(true);

    // Load Razorpay script if not already loaded
    const scriptLoaded = await loadRazorpayScript();
    
    if (!scriptLoaded) {
      setErrorMessage('Failed to load payment gateway. Please try again.');
      setStep('error');
      setIsProcessing(false);
      return;
    }

    // Initialize Razorpay payment
    initializeRazorpay(
      formData,
      (response) => {
        // Payment successful
        setPaymentId(response.razorpay_payment_id);
        setStep('success');
        setIsProcessing(false);
        
        // Send confirmation via WhatsApp
        sendBookingConfirmation(formData, response.razorpay_payment_id, calculatedAmount);
      },
      (error) => {
        // Payment failed
        setErrorMessage(error.message || 'Payment failed. Please try again.');
        setStep('error');
        setIsProcessing(false);
      }
    );
  };

  const handleClose = () => {
    setStep('details');
    setFormData({
      name: '',
      email: '',
      phone: '',
      checkIn: '',
      checkOut: '',
      guests: 1,
      roomType: preselectedRoomType || 'Royal',
      specialRequests: '',
      idProofType: '',
      idProofNumber: '',
    });
    setPaymentId('');
    setErrorMessage('');
    onClose();
  };

  const calculateNights = () => {
    if (!formData.checkIn || !formData.checkOut) return 0;
    const checkIn = new Date(formData.checkIn);
    const checkOut = new Date(formData.checkOut);
    const nights = Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(nights, 1);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-navy-900 rounded-3xl shadow-2xl">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 text-cream-300 hover:text-gold-400 hover:bg-gold-400/10 rounded-full transition-colors z-10"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Step 1: Booking Details */}
        {step === 'details' && (
          <div className="p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gold-400 mb-2">Book Your Stay</h2>
              <p className="text-cream-200">Fill in your details to proceed with booking</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Details */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gold-400 mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-lg text-white placeholder-cream-300 focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    placeholder="Enter your name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gold-400 mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-lg text-white placeholder-cream-300 focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    placeholder="+91 XXXXXXXXXX"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gold-400 mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-lg text-white placeholder-cream-300 focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                  placeholder="your@email.com"
                />
              </div>

              {/* ID Proof Details */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gold-400 mb-2">ID Proof Type *</label>
                  <select
                    name="idProofType"
                    value={formData.idProofType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-lg text-white focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                  >
                    <option value="" className="bg-navy-900">Select ID Proof</option>
                    <option value="aadhar" className="bg-navy-900">Aadhar Card</option>
                    <option value="driving_license" className="bg-navy-900">Driving License</option>
                    <option value="pan_card" className="bg-navy-900">PAN Card</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gold-400 mb-2">ID Proof Number *</label>
                  <input
                    type="text"
                    name="idProofNumber"
                    required
                    value={formData.idProofNumber}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-lg text-white placeholder-cream-300 focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    placeholder="Enter ID number"
                  />
                </div>
              </div>

              {/* Booking Details */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gold-400 mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Check-in Date *
                  </label>
                  <input
                    type="date"
                    name="checkIn"
                    required
                    value={formData.checkIn}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-lg text-white focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    style={{ colorScheme: 'dark' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gold-400 mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Check-out Date *
                  </label>
                  <input
                    type="date"
                    name="checkOut"
                    required
                    value={formData.checkOut}
                    onChange={handleChange}
                    min={formData.checkIn || new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-lg text-white focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    style={{ colorScheme: 'dark' }}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gold-400 mb-2 flex items-center gap-2">
                    <Bed className="w-4 h-4" />
                    Room Type *
                  </label>
                  <select
                    name="roomType"
                    value={formData.roomType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-lg text-white focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                  >
                    <option value="Single" className="bg-navy-900">Single Room - ₹{ROOM_PRICES.Single}/night</option>
                    <option value="Royal" className="bg-navy-900">Royal Room - ₹{ROOM_PRICES.Royal}/night</option>
                    <option value="Suite" className="bg-navy-900">Suite Room - ₹{ROOM_PRICES.Suite}/night</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gold-400 mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Number of Guests *
                  </label>
                  <select
                    name="guests"
                    value={formData.guests}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-lg text-white focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                  >
                    {[1, 2, 3, 4].map(num => (
                      <option key={num} value={num} className="bg-navy-900">{num} {num === 1 ? 'Guest' : 'Guests'}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gold-400 mb-2">Special Requests (Optional)</label>
                <textarea
                  name="specialRequests"
                  value={formData.specialRequests}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-lg text-white placeholder-cream-300 focus:ring-2 focus:ring-gold-500 focus:border-transparent resize-none"
                  placeholder="Any special requirements?"
                />
              </div>

              {/* Price Preview */}
              {calculatedAmount > 0 && (
                <div className="bg-gold-gradient rounded-lg p-4 text-navy-900">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Estimated Total:</span>
                    <span className="text-2xl font-bold">₹{calculatedAmount.toLocaleString('en-IN')}</span>
                  </div>
                  <p className="text-sm mt-1">For {calculateNights()} night(s) • Including GST</p>
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-gold-gradient text-navy-900 py-4 rounded-lg font-bold text-lg hover:shadow-gold-xl transition-all transform hover:scale-105"
              >
                Continue to Payment
              </button>
            </form>
          </div>
        )}

        {/* Step 2: Booking Summary */}
        {step === 'summary' && (
          <div className="p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gold-400 mb-2">Confirm Your Booking</h2>
              <p className="text-cream-200">Review your details before payment</p>
            </div>

            <div className="space-y-6">
              {/* Guest Details */}
              <div className="bg-white/5 rounded-lg p-6 border border-gold-400/20">
                <h3 className="text-lg font-bold text-gold-400 mb-4">Guest Details</h3>
                <div className="space-y-2 text-cream-200">
                  <p><span className="text-cream-400">Name:</span> {formData.name}</p>
                  <p><span className="text-cream-400">Email:</span> {formData.email}</p>
                  <p><span className="text-cream-400">Phone:</span> {formData.phone}</p>
                </div>
              </div>

              {/* Booking Details */}
              <div className="bg-white/5 rounded-lg p-6 border border-gold-400/20">
                <h3 className="text-lg font-bold text-gold-400 mb-4">Booking Details</h3>
                <div className="space-y-2 text-cream-200">
                  <p><span className="text-cream-400">Room Type:</span> {formData.roomType}</p>
                  <p><span className="text-cream-400">Check-in:</span> {new Date(formData.checkIn).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                  <p><span className="text-cream-400">Check-out:</span> {new Date(formData.checkOut).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                  <p><span className="text-cream-400">Nights:</span> {calculateNights()}</p>
                  <p><span className="text-cream-400">Guests:</span> {formData.guests}</p>
                  {formData.specialRequests && (
                    <p><span className="text-cream-400">Special Requests:</span> {formData.specialRequests}</p>
                  )}
                </div>
              </div>

              {/* Payment Summary */}
              <div className="bg-gold-gradient rounded-lg p-6">
                <h3 className="text-lg font-bold text-navy-900 mb-4">Payment Summary</h3>
                <div className="space-y-2 text-navy-900">
                  <div className="flex justify-between">
                    <span>Room Charges ({calculateNights()} nights)</span>
                    <span>₹{(ROOM_PRICES[formData.roomType] * calculateNights()).toLocaleString('en-IN')}</span>
                  </div>
                  {formData.guests > 2 && (
                    <div className="flex justify-between">
                      <span>Extra Guest Charges</span>
                      <span>₹{((formData.guests - 2) * (formData.roomType === 'Suite' ? 500 : formData.roomType === 'Royal' ? 500 : 400) * calculateNights()).toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>GST (5%)</span>
                    <span>₹{Math.round((calculatedAmount / 1.05) * 0.05).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="border-t-2 border-navy-900/20 pt-2 mt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold">Total Amount</span>
                      <span className="text-2xl font-bold">₹{calculatedAmount.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Terms & Conditions Checkbox */}
              <div className="bg-white/5 rounded-lg p-4 border border-gold-400/20 mb-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-gold-400/50 text-gold-400 focus:ring-gold-400"
                  />
                  <span className="text-sm text-cream-200">
                    I agree to the{' '}
                    <a
                      href="#policies"
                      onClick={(e) => {
                        e.preventDefault();
                        onClose();
                        document.getElementById('policies')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="text-gold-400 hover:text-gold-300 underline"
                    >
                      cancellation policy
                    </a>
                    {' '}and understand that 10% deduction applies for cancellations made within 24 hours of booking.
                  </span>
                </label>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setStep('details')}
                  className="flex-1 bg-white/10 text-cream-200 py-4 rounded-lg font-bold hover:bg-white/20 transition-all"
                >
                  Back
                </button>
                <button
                  onClick={handlePayment}
                  disabled={isProcessing || !acceptedTerms}
                  className="flex-1 bg-gold-gradient text-navy-900 py-4 rounded-lg font-bold hover:shadow-gold-xl transition-all transform hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isProcessing ? (
                    <>
                      <div className="w-5 h-5 border-2 border-navy-900 border-t-transparent rounded-full animate-spin"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      Pay ₹{calculatedAmount.toLocaleString('en-IN')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 'success' && (
          <div className="p-8 text-center">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gold-400 mb-4">Booking Confirmed!</h2>
            <p className="text-cream-200 mb-6">Your payment was successful and booking is confirmed.</p>
            
            <div className="bg-white/5 rounded-lg p-6 border border-gold-400/20 mb-6">
              <p className="text-sm text-cream-400 mb-2">Payment ID</p>
              <p className="text-gold-400 font-mono font-bold">{paymentId}</p>
            </div>

            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-6">
              <p className="text-green-400 text-sm">
                A confirmation message has been sent to our team via WhatsApp. You will receive a confirmation email shortly.
              </p>
            </div>

            <button
              onClick={handleClose}
              className="w-full bg-gold-gradient text-navy-900 py-4 rounded-lg font-bold hover:shadow-gold-xl transition-all"
            >
              Close
            </button>
          </div>
        )}

        {/* Step 4: Error */}
        {step === 'error' && (
          <div className="p-8 text-center">
            <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-red-400 mb-4">Payment Failed</h2>
            <p className="text-cream-200 mb-6">{errorMessage}</p>

            <div className="flex gap-4">
              <button
                onClick={() => setStep('details')}
                className="flex-1 bg-white/10 text-cream-200 py-4 rounded-lg font-bold hover:bg-white/20 transition-all"
              >
                Try Again
              </button>
              <button
                onClick={handleClose}
                className="flex-1 bg-gold-gradient text-navy-900 py-4 rounded-lg font-bold hover:shadow-gold-xl transition-all"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
